import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { soloApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";
import { AGENTS } from "@/lib/agents";

const QUESTIONS = [
  { key: "business_name", label: "What are you building? (working title is fine)", placeholder: "e.g. Loopcup — reusable coffee cups" },
  { key: "niche", label: "Who's it for?", placeholder: "e.g. coffee shops in Berlin" },
  { key: "stage", label: "Where are you right now?", options: [
    { value: 0, label: "Just an idea", tag: "Stage 0 · Co-founder mode" },
    { value: 1, label: "Launching soon", tag: "Stage 1 · Launch mode" },
    { value: 2, label: "Already earning", tag: "Stage 2 · Operator mode" },
  ]},
  { key: "goal", label: "What's the one thing you want to be true 30 days from now?", placeholder: "e.g. 3 paying pilot customers" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { refetch } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ business_name: "", niche: "", stage: 0, goal: "" });
  const [saving, setSaving] = useState(false);

  const q = QUESTIONS[step];
  const canNext = q.key === "stage" ? true : (answers[q.key] || "").toString().trim().length > 0;

  const submit = async () => {
    setSaving(true);
    try {
      await soloApi.saveOnboarding(answers);
      await refetch();
      nav("/dashboard");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen aurora relative overflow-hidden grain flex items-center justify-center p-6">
      <div className="pointer-events-none absolute -top-16 -left-16 w-72 h-72 bg-neon-pink/40 blur-3xl slime-blob" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 bg-neon-purple/40 blur-3xl slime-blob" />

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-2xl glass rounded-[2rem] p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 slime-blob overflow-hidden" style={{ background: AGENTS.ceo.gradient }}>
            <img src={AGENTS.ceo.avatar} alt="Casey" className="w-full h-full object-cover mix-blend-luminosity" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-neon-chrome">Casey · CEO</div>
            <div className="font-display text-xl font-bold">Hey! Let's spin up your crew.</div>
          </div>
          <Sparkles className="ml-auto text-neon-pink animate-pulse" />
        </div>

        <div className="mb-2 font-mono text-xs text-white/60">Step {step + 1} of {QUESTIONS.length}</div>
        <div className="h-1.5 w-full bg-white/10 rounded-full mb-6 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-neon-pink to-neon-cyan transition-all" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.key}
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <label className="block font-display text-2xl font-bold mb-4">{q.label}</label>

            {q.options ? (
              <div className="grid gap-3">
                {q.options.map((op) => (
                  <button
                    key={op.value}
                    data-testid={`onboard-stage-${op.value}`}
                    onClick={() => setAnswers({ ...answers, stage: op.value })}
                    className={"text-left p-4 rounded-2xl border-2 transition-colors " + (answers.stage === op.value ? "border-neon-pink bg-white/10" : "border-white/15 hover:border-white/30")}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neon-cyan mb-1">{op.tag}</div>
                    <div className="font-semibold text-white">{op.label}</div>
                  </button>
                ))}
              </div>
            ) : q.key === "goal" ? (
              <Textarea
                data-testid={`onboard-input-${q.key}`}
                value={answers[q.key]}
                onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                placeholder={q.placeholder}
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl min-h-[100px]"
              />
            ) : (
              <Input
                data-testid={`onboard-input-${q.key}`}
                value={answers[q.key]}
                onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                placeholder={q.placeholder}
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12"
              />
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <Button data-testid="onboard-back" variant="ghost" onClick={() => setStep(step - 1)} className="text-white hover:bg-white/10 rounded-full">Back</Button>
          )}
          {step < QUESTIONS.length - 1 ? (
            <Button data-testid="onboard-next" disabled={!canNext} onClick={() => setStep(step + 1)} className="ml-auto rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold px-6 neon-glow-pink">
              Next →
            </Button>
          ) : (
            <Button data-testid="onboard-finish" disabled={!canNext || saving} onClick={submit} className="ml-auto rounded-full bg-neon-lime hover:bg-neon-lime/90 text-slate-900 font-bold px-6 neon-glow-lime">
              {saving ? "Spinning up crew…" : "Meet the crew ✦"}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
